import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-[color:var(--navy)] mb-2">Privacy Policy</h1>
            <p className="text-slate-500">Last Updated: April 18, 2026</p>
          </div>

          <div className="space-y-6 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">INTRODUCTION</h2>
              <p>
                Pasco Appliance LLC ("we," "us," or "our") respects your privacy and is committed to protecting it through our compliance with this policy. This policy describes the types of information we may collect from you or that you may provide when you visit the website pascoappliance.com (our "Website") and our practices for collecting, using, maintaining, protecting, and disclosing that information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">INFORMATION WE COLLECT</h2>
              <p className="mb-3">
                We collect several types of information from and about users of our Website, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Personal Information:</strong> By which you may be personally identified, such as name, postal address, e-mail address, and telephone number ("personal information").</li>
                <li><strong>Service Data:</strong> Information regarding your household appliances and service history.</li>
                <li><strong>Usage Details:</strong> Information about your internet connection, the equipment you use to access our Website, and usage details.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">SMS AND MOBILE COMMUNICATION (CRITICAL COMPLIANCE)</h2>
              <p className="mb-3">
                By providing your mobile number to Pasco Appliance LLC, you agree to receive service-related text messages (SMS/MMS), such as appointment confirmations, "on my way" notifications, and invoices.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Opt-In:</strong> Mobile information is collected only when you voluntarily provide it through our website forms, chat assistant, or phone calls.</li>
                <li><strong>No Third-Party Sharing:</strong> No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</li>
                <li><strong>Opt-Out:</strong> You can stop receiving text messages at any time by replying "STOP" to any message you receive from us.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">USE OF YOUR INFORMATION</h2>
              <p className="mb-3">
                We use information that we collect about you or that you provide to us, including any personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide you with information, products, or services that you request from us.</li>
                <li>To schedule and fulfill appliance repair services at your location.</li>
                <li>To carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">DATA SECURITY</h2>
              <p>
                We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure. All payment transactions are processed through encrypted third-party providers (e.g., Square).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[color:var(--navy)] mb-3">CONTACT INFORMATION</h2>
              <p className="mb-3">
                To ask questions or comment about this privacy policy and our privacy practices, contact us at:
              </p>
              <div className="pl-4 space-y-1">
                <p className="font-semibold">Pasco Appliance LLC</p>
                <p>Odessa, FL 33556</p>
                <p>Email: <a href="mailto:contact@pascoappliance.com" className="text-[color:var(--safety-orange)] underline">contact@pascoappliance.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Pasco Appliance LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
